import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { finalize } from "rxjs/operators";
import { environment as appEnv } from "../../../environments/environment";
import { Services } from "../../integration/services";
import { RoutesStatsService } from "../../models/routes-stats.service";
import { PlanTierService } from "../../plan-tier/plan-tier.service";
import { RepositoryUrlService } from "../../repository-url/repository-url.service";
import { TimeDurAgoService } from "../../time-dur-ago/time-dur-ago.service";
import { UpgradeModalService } from "../../upgrade-modal/upgrade-modal.service";
import { sum } from "../../utils/sum";

@Component({
  selector: "ab-notice-overview",
  templateUrl: "./notice-overview.component.html",
})
export class NoticeOverviewComponent implements OnInit {
  @Input() public project: any;
  @Input() public group: any;
  @Input() public notice: any;
  @Input() public chart: any;
  @Input() public dur: string;
  @Input() public lastN: number;
  @Input() public loadingChart: boolean;
  @Input() public deployRevision: string;
  @Input() public deletingGroup: boolean;
  @Input() public groupDeployAvailable: boolean;
  @Output() public loadChart = new EventEmitter();
  @Output() public groupMuted = new EventEmitter();
  @Output() public groupUnmuted = new EventEmitter();
  @Output() public groupDeleted = new EventEmitter();

  public appEnv = appEnv;
  public repoUrl: string;
  public routeStats: any;
  public integrationsEnabled: boolean;
  public featuresLoading: boolean;
  public mutingEnabled: boolean;
  public deployDashEnabled: boolean;
  public showConfigureIntegrationsButton: boolean;
  public shouldShowIntegrationButtons: boolean;
  public showRouteApmCta: boolean;

  constructor(
    private planTierService: PlanTierService,
    private repositoryUrlService: RepositoryUrlService,
    private routesStatsService: RoutesStatsService,
    private timeDurAgoService: TimeDurAgoService,
    private upgradeModal: UpgradeModalService,
  ) {}

  public ngOnInit() {
    this.repoUrl = this.repositoryUrlService.findForProject(this.project);
    this.setRouteDisplayInfo();

    this.featuresLoading = true;
    this.planTierService
      .features()
      .pipe(finalize(() => (this.featuresLoading = false)))
      .subscribe((features: any) => {
        this.integrationsEnabled = features.integrations;
        this.mutingEnabled = !!(features.muting || this.group.muted);
        this.deployDashEnabled = features.deploy_dashboard;

        this.showConfigureIntegrationsButton =
          this.integrationsEnabled &&
          !this.issueServiceIntegrationsConfigured();
        this.shouldShowIntegrationButtons =
          this.integrationsEnabled && this.issueServiceIntegrationsConfigured();
      });
  }

  public trackByIndex(index: number, _item: any) {
    return index;
  }

  public onTimeDurChange($event: any) {
    this.dur = $event.dur;
    this.lastN = $event.last_n;
    this.loadChart.emit($event);
    this.setRouteDisplayInfo();
  }

  public openUpgradeModal($event: any) {
    this.upgradeModal.open($event);
  }

  public muteGroup(group: any) {
    this.groupMuted.emit(group);
  }

  public unmuteGroup(group: any) {
    this.groupUnmuted.emit(group);
  }

  public deleteGroup() {
    this.groupDeleted.emit();
  }

  public isIssueService(integrationName: string) {
    return Services.ISSUE.indexOf(integrationName) >= 0;
  }

  private setRouteDisplayInfo() {
    // No route info whatsoever to display.
    if (!this.notice.context.route) {
      return;
    }

    // Show a CTA to start using APM when we have "route" info but the project
    // is not sending performance data.
    this.showRouteApmCta = !this.project.lastRequestAt;

    const routeStatsPresent =
      this.notice.context.httpMethod && this.notice.context.routeId;
    if (!routeStatsPresent) {
      return;
    }

    this.fetchRouteStats().subscribe((data: any) => {
      const noticeCount = sum(data.noticeCount);

      const routeStats = {
        requestCount: sum(data.total.count),
        errorRate: undefined as number,
      };

      // Nothing to display if we can't calculate error rate.
      if (routeStats.requestCount <= 0) {
        this.routeStats = undefined;
        return;
      }

      routeStats.errorRate = (noticeCount / routeStats.requestCount) * 100;

      this.routeStats = routeStats;
    });
  }

  private fetchRouteStats() {
    const period = (() => {
      switch (this.dur.split("/")[1]) {
        case "1d":
          return "day";
        case "1h":
          return "hour";
        case "1m":
          return "minute";
      }
    })();
    const timestamp = this.timeDurAgoService.timeFor(this.dur, this.lastN);

    const routeStatsParams = {
      projectId: this.project.id,
      routeId: this.notice.context.routeId,
      time__gte: timestamp,
      period,
    };

    return this.routesStatsService.get(routeStatsParams);
  }

  private issueServiceIntegrationsConfigured() {
    if (this.project.integrations == null) {
      return false;
    }

    for (const serviceName in this.project.integrations) {
      if (this.isIssueService(serviceName)) {
        return true;
      }
    }

    return false;
  }
}
